/**
 * Shared presentation pieces.
 *
 * `ErrorNotice` is the one worth reading: it renders the API's error envelope,
 * including findings, and gives conflicts their own treatment. Reporting "someone
 * else changed this, here is what differs" rather than silently merging is the
 * decision from A4, and the UI has to actually show it for that to mean anything.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";

import { ApiError } from "./api/client";

export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;

  if (error instanceof ApiError && error.isConflict) {
    const expected = error.detail.expected_revision;
    const actual = error.detail.actual_revision;
    return (
      <div className="conflict" role="alert">
        <strong>Someone else changed this first.</strong>
        <div>
          You were editing revision {String(expected)}; it is now revision {String(actual)}.
          Reload to see their changes, then reapply yours.
        </div>
      </div>
    );
  }

  if (error instanceof ApiError) {
    return (
      <div className="error" role="alert">
        <strong>{error.message}</strong>
        {error.findings.length > 0 && (
          <ul className="findings">
            {error.findings.map((finding, index) => (
              <li key={`${finding.code}-${index}`}>
                {finding.path ? <code>{finding.path}</code> : null} {finding.message}
              </li>
            ))}
          </ul>
        )}
        {typeof error.detail.reference === "string" && (
          <div className="muted">Reference: {error.detail.reference}</div>
        )}
      </div>
    );
  }

  return (
    <div className="error" role="alert">
      {error instanceof Error ? error.message : "Something went wrong."}
    </div>
  );
}

export function Loading({ what }: { what: string }) {
  return <p className="muted">Loading {what}…</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted">{children}</p>;
}

export function Timestamp({ value }: { value: string }) {
  const date = new Date(value);
  return <span title={date.toISOString()}>{date.toLocaleString()}</span>;
}

const RELATIVE_STEPS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 7],
  ["week", 4.35],
  ["month", 12],
  ["year", Infinity],
];

/** "3 hours ago", in the browser's language. */
export function relativeTime(value: string, now: number = Date.now()): string {
  let amount = (new Date(value).getTime() - now) / 1000;
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of RELATIVE_STEPS) {
    if (Math.abs(amount) < size) return format.format(Math.round(amount), unit);
    amount /= size;
  }
  return format.format(Math.round(amount), "year");
}

/** A time as "3 hours ago", with the exact time as a tooltip and in the markup. */
export function RelativeTime({ value }: { value: string }) {
  const date = new Date(value);
  return (
    <time dateTime={date.toISOString()} title={date.toLocaleString()}>
      {relativeTime(value)}
    </time>
  );
}

export function ProgressBar({ value }: { value: number }) {
  const percent = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div style={{ width: `${percent}%` }} />
    </div>
  );
}

/**
 * A modal confirmation for something that cannot be undone.
 *
 * Focus starts on Cancel, so Enter or a stray keypress never confirms; Escape
 * and a click outside cancel. Tab stays inside the dialog while it is open.
 * The error from a refused attempt is shown in place, so the reason (for
 * example, published work) is read where the question was asked.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  busy = false,
  busyLabel = "Deleting…",
  danger = true,
  focusField = false,
  confirmDisabled = false,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  /** The confirm button's label while `busy`. */
  busyLabel?: string;
  /** Styled as destructive. Off for a question that changes something recoverable. */
  danger?: boolean;
  /** Start on the first field instead of Cancel: for a dialog that asks for input. */
  focusField?: boolean;
  confirmDisabled?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const field = focusField ? dialogRef.current?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled]), textarea:not([disabled])")
      : null;
    (field ?? cancelRef.current)?.focus();
    return () => previous?.focus?.();
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!busy) onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])",
      ) ?? [])];
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !busy && onCancel()}>
      <div
        ref={dialogRef}
        className="dialog card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={onKeyDown}
      >
        <h3 id={titleId}>{title}</h3>
        <div id={bodyId} className="stack">
          {children}
        </div>
        <ErrorNotice error={error} />
        <div className="row dialog-actions">
          <button ref={cancelRef} type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? "danger" : "primary"}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A small icon button for removing a row. Named by `label`, shown as a tooltip too. */
export function RemoveButton({
  label,
  disabledReason,
  onClick,
}: {
  label: string;
  disabledReason?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="icon-button"
      aria-label={label}
      title={disabledReason ?? label}
      disabled={disabledReason !== undefined}
      onClick={onClick}
    >
      <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth={2} strokeLinecap="round" fill="none" />
      </svg>
    </button>
  );
}

/** How long a passing notice stays before it goes by itself. */
export const NOTICE_DISMISS_MS = 8000;

/**
 * A banner the designer can close. With `autoDismissMs` it also goes by
 * itself, but never while the pointer is over it or focus is inside it, so it
 * cannot vanish while being read or acted on (WCAG 2.2.1). Things that need a
 * decision, such as a conflict, are not banners: they stay until decided.
 */
export function Banner({
  className,
  role = "status",
  label,
  autoDismissMs,
  resetKey,
  onDismiss,
  children,
}: {
  className: string;
  role?: "status" | "alert";
  label?: string;
  autoDismissMs?: number;
  /** A new value restarts the countdown: a different message gets its full time. */
  resetKey?: string;
  onDismiss: () => void;
  children: ReactNode;
}) {
  const [held, setHeld] = useState(false);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useEffect(() => {
    if (!autoDismissMs || held) return;
    const timer = window.setTimeout(() => dismiss.current(), autoDismissMs);
    return () => window.clearTimeout(timer);
  }, [autoDismissMs, held, resetKey]);
  return (
    <div
      className={`banner ${className}`}
      role={role}
      aria-label={label}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHeld(false);
      }}
    >
      <div className="banner-body">{children}</div>
      <button type="button" className="banner-close" aria-label="Dismiss" title="Dismiss" onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}
